import { collection, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { CACHE_KEYS } from './cache/cacheKeys';
import { dataCache } from './cache/dataCache';
import { DashboardService } from './DashboardService';

export interface UserPreferences {
  language?: string;
  notifications?: boolean;
}

export interface UserSubscription {
  subscriptionStatus?: string;
  subscriptionType?: string;
  subscriptionDate?: string;
  proExpiryDate?: string;
  proActivatedAt?: string;
  subscriptionDuration?: number;
  isSandbox?: boolean;
  lastTransactionId?: string;
  originalTransactionId?: string;
}

export interface AppUser {
  id: string;
  username?: string;
  email?: string;
  emailVerified?: boolean;
  isPro?: boolean;
  status?: string;
  privacyAndTermsCheckbox?: boolean;
  preferences?: UserPreferences;
  pushToken?: string;
  createdAt?: string;
  lastLoginAt?: string;
  proActivatedAt?: string;
  subscription?: UserSubscription;
  updatedAt?: string | { seconds: number; nanoseconds?: number };
}

export interface UserUpdatePayload {
  username?: string;
  email?: string;
  status?: string;
  emailVerified?: boolean;
  isPro?: boolean;
  proActivatedAt?: string | null;
  preferences?: UserPreferences;
  subscription?: UserSubscription | null;
}

export class UserService {
  static async getUsers(): Promise<AppUser[]> {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const users = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as AppUser[];

      return users.sort((a, b) => {
        const dateA = parseUserDate(a.createdAt)?.getTime() ?? 0;
        const dateB = parseUserDate(b.createdAt)?.getTime() ?? 0;
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
      return [];
    }
  }

  static async getUserById(userId: string): Promise<AppUser | null> {
    const cacheKey = `${CACHE_KEYS.users}:${userId}`;
    const cached = dataCache.get<AppUser>(cacheKey, 60 * 1000);
    if (cached) return cached;

    const listCached = dataCache.get<AppUser[]>(CACHE_KEYS.users, 3 * 60 * 1000);
    const fromList = listCached?.find((user) => user.id === userId);
    if (fromList) {
      dataCache.set(cacheKey, fromList);
      return fromList;
    }

    try {
      const snap = await getDoc(doc(db, 'users', userId));
      if (!snap.exists()) return null;
      const user = { id: snap.id, ...snap.data() } as AppUser;
      dataCache.set(cacheKey, user);
      return user;
    } catch (error) {
      console.error('Kullanıcı yüklenirken hata:', error);
      return null;
    }
  }

  static async updateUser(userId: string, updates: UserUpdatePayload): Promise<boolean> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        username: updates.username,
        email: updates.email,
        status: updates.status,
        emailVerified: updates.emailVerified,
        isPro: updates.isPro,
        proActivatedAt: updates.proActivatedAt,
        preferences: updates.preferences,
        subscription: updates.subscription,
        updatedAt: new Date().toISOString(),
      });
      this.invalidateUserCache(userId);
      return true;
    } catch (error) {
      console.error('Kullanıcı güncellenirken hata:', error);
      return false;
    }
  }

  static invalidateUserCache(userId?: string): void {
    dataCache.invalidate(CACHE_KEYS.users);
    DashboardService.invalidateStats();
    if (userId) {
      dataCache.invalidate(`${CACHE_KEYS.users}:${userId}`);
    }
  }
}

export const parseUserDate = (
  value?: string | { seconds: number; nanoseconds?: number } | null
): Date | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'object' && 'seconds' in value) {
    return new Date(value.seconds * 1000);
  }
  return null;
};

export const formatUserDate = (
  value?: string | { seconds: number; nanoseconds?: number } | null
): string => {
  const parsed = parseUserDate(value);
  return parsed ? parsed.toLocaleString('tr-TR') : '—';
};

export const toDatetimeLocalValue = (iso?: string | null): string => {
  const parsed = parseUserDate(iso ?? undefined);
  if (!parsed) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

export const fromDatetimeLocalValue = (value: string): string | null => {
  if (!value.trim()) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

export const getUserDisplayName = (user: AppUser): string =>
  user.username?.trim() || user.email?.split('@')[0] || 'Kullanıcı';

export const getUserInitials = (user: AppUser): string =>
  getUserDisplayName(user)
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || '?';

export const getSubscriptionLabel = (user: AppUser): string => {
  if (!user.isPro) return 'Free';
  const type = user.subscription?.subscriptionType;
  const status = user.subscription?.subscriptionStatus;
  if (type && status) return `Pro · ${type} (${status})`;
  return 'Pro';
};
